/**
 * Sensitive fields filtering utilities.
 *
 * Shared between controllers and services to filter out sensitive fields
 * (passwords, tokens, secrets) from API responses.
 */

import type { PaginatedResult } from '@gzl10/nexus-sdk'

/**
 * Exclude sensitive fields from a single entity.
 *
 * @param data - Entity data
 * @param sensitiveFields - Field names to exclude
 * @returns Entity without sensitive fields
 */
export function excludeSensitiveFields<T extends Record<string, unknown>>(
  data: T,
  sensitiveFields: string[]
): Partial<T> {
  if (sensitiveFields.length === 0) return data

  const result = { ...data }
  for (const field of sensitiveFields) {
    delete result[field]
  }
  return result
}

/**
 * Exclude sensitive fields from an array of entities.
 *
 * @param items - Array of entities
 * @param sensitiveFields - Field names to exclude
 * @returns Array of entities without sensitive fields
 */
export function excludeSensitiveFieldsFromArray<T extends Record<string, unknown>>(
  items: T[],
  sensitiveFields: string[]
): Partial<T>[] {
  if (sensitiveFields.length === 0) return items
  return items.map(item => excludeSensitiveFields(item, sensitiveFields))
}

/**
 * Exclude sensitive fields from a paginated result.
 *
 * @param result - Paginated result
 * @param sensitiveFields - Field names to exclude
 * @returns Paginated result with filtered items
 */
export function excludeSensitiveFieldsFromResult<T extends Record<string, unknown>>(
  result: PaginatedResult<T>,
  sensitiveFields: string[]
): PaginatedResult<Partial<T>> {
  if (sensitiveFields.length === 0) return result
  return {
    ...result,
    items: excludeSensitiveFieldsFromArray(result.items, sensitiveFields)
  }
}
